/**
 * VideoSync.js - Multi-video synchronization system
 * Allows users to upload videos, assign to displays, and monitor frame-perfect sync
 */

import React, { useState, useEffect } from 'react';
import axiosInstance from './axiosInstance';
import { getSocket } from './socket';
import Sidebar from './Sidebar';
import './styles/VideoSync.css';
import ProtectedComponent from './ProtectedComponent';

// Import sub-components
import VideoLibrary from './components/VideoSync/VideoLibrary';
import DisplayManager from './components/VideoSync/DisplayManager';
import SessionCreator from './components/VideoSync/SessionCreator';
import SyncMonitor from './components/VideoSync/SyncMonitor';
import DisplayControl from './components/VideoSync/DisplayControl';
import VideoWallEditor from './components/VideoSync/VideoWallEditor';

const VideoSync = () => {
  const [activeTab, setActiveTab] = useState('videos');
  const [videos, setVideos] = useState([]);
  const [displays, setDisplays] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [videoWalls, setVideoWalls] = useState([]);
  const [loading, setLoading] = useState(false);

  // Use global socket connection
  useEffect(() => {
    const socket = getSocket();

    if (!socket) {
      console.warn('[VideoSync] Global socket not initialized yet');
      return;
    }

    console.log('[VideoSync] Using global WebSocket connection');

    // Join video-sync room
    socket.emit('video-sync:join');

    // Listen for real-time updates
    const handleDisplayStatus = (data) => {
      console.log('[VideoSync] Display status update:', data);
      // Update display status in real-time
      setDisplays(prev => prev.map(d =>
        d.displayId === data.displayId
          ? { ...d, ...data }
          : d
      ));
    };

    const handleMetricsUpdate = (data) => {
      console.log('[VideoSync] Session metrics update:', data);
    };

    const handleDriftAlert = (data) => {
      console.log('[VideoSync] Drift alert:', data);
      // Show notification
      alert(`Drift Alert! Display ${data.displayId} has ${data.drift}ms drift`);
    };

    const handleEncodingComplete = (data) => {
      console.log('[VideoSync] Video encoding complete:', data);
      // Update video status in real-time
      setVideos(prev => prev.map(v =>
        v._id === data.videoId
          ? { ...v, status: data.status }
          : v
      ));
    };

    // Attach event listeners
    socket.on('display:status', handleDisplayStatus);
    socket.on('session:metrics-update', handleMetricsUpdate);
    socket.on('session:drift-alert', handleDriftAlert);
    socket.on('video:encoding:complete', handleEncodingComplete);

    return () => {
      // Clean up event listeners (but don't disconnect global socket!)
      socket.off('display:status', handleDisplayStatus);
      socket.off('session:metrics-update', handleMetricsUpdate);
      socket.off('session:drift-alert', handleDriftAlert);
      socket.off('video:encoding:complete', handleEncodingComplete);
      socket.emit('video-sync:leave');
    };
  }, []);

  // Fetch initial data
  useEffect(() => {
    fetchVideos();
    fetchDisplays();
    fetchSessions();
    fetchVideoWalls();
  }, []);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/video-sync/videos');
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error('[VideoSync] Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDisplays = async () => {
    try {
      const response = await axiosInstance.get('/video-sync/displays');
      setDisplays(response.data.displays || []);
    } catch (error) {
      console.error('[VideoSync] Error fetching displays:', error);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await axiosInstance.get('/video-sync/sessions');
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('[VideoSync] Error fetching sessions:', error);
    }
  };

  const fetchVideoWalls = async () => {
    try {
      const response = await axiosInstance.get('/video-sync/video-walls');
      setVideoWalls(response.data.videoWalls || []);
    } catch (error) {
      console.error('[VideoSync] Error fetching video walls:', error);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'videos':
        return (
          <VideoLibrary
            videos={videos}
            onRefresh={fetchVideos}
            loading={loading}
          />
        );
      case 'displays':
        return (
          <DisplayManager
            displays={displays}
            onRefresh={fetchDisplays}
          />
        );
      case 'sessions':
        return (
          <SessionCreator
            sessions={sessions}
            videos={videos}
            displays={displays}
            onRefresh={fetchSessions}
          />
        );
      case 'monitor':
        return (
          <SyncMonitor
            sessions={sessions}
            socket={getSocket()}
          />
        );
      case 'control':
        return (
          <DisplayControl
            displays={displays}
          />
        );
      case 'videowall':
        return (
          <VideoWallEditor
            displays={displays}
            videoWalls={videoWalls}
            onRefresh={() => {
              fetchVideoWalls();
              fetchDisplays();
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ProtectedComponent functionCode="OP08" functionName="Video Sync">
      <div className="video-sync-container">
      <Sidebar />

      <div className="video-sync-main">
        <div className="video-sync-header">
          <h1>Multi-Video Synchronization System</h1>
          <p className="subtitle">Upload videos, assign to displays, and achieve frame-perfect synchronization</p>
        </div>

        <div className="video-sync-tabs">
          <button
            className={`tab-button ${activeTab === 'videos' ? 'active' : ''}`}
            onClick={() => setActiveTab('videos')}
          >
            <span className="tab-icon">🎬</span>
            Video Library
            <span className="tab-badge">{videos.length}</span>
          </button>

          <button
            className={`tab-button ${activeTab === 'displays' ? 'active' : ''}`}
            onClick={() => setActiveTab('displays')}
          >
            <span className="tab-icon">📺</span>
            Displays
            <span className="tab-badge">{displays.length}</span>
          </button>

          <button
            className={`tab-button ${activeTab === 'sessions' ? 'active' : ''}`}
            onClick={() => setActiveTab('sessions')}
          >
            <span className="tab-icon">🎯</span>
            Sessions
            <span className="tab-badge">{sessions.length}</span>
          </button>

          <button
            className={`tab-button ${activeTab === 'monitor' ? 'active' : ''}`}
            onClick={() => setActiveTab('monitor')}
          >
            <span className="tab-icon">📊</span>
            Live Monitor
          </button>

          <button
            className={`tab-button ${activeTab === 'control' ? 'active' : ''}`}
            onClick={() => setActiveTab('control')}
          >
            <span className="tab-icon">🎮</span>
            Display Control
          </button>

          <button
            className={`tab-button ${activeTab === 'videowall' ? 'active' : ''}`}
            onClick={() => setActiveTab('videowall')}
          >
            <span className="tab-icon">🖼️</span>
            Video Wall
            <span className="tab-badge">{videoWalls.length}</span>
          </button>
        </div>

        <div className="video-sync-content">
          {renderTabContent()}
        </div>
      </div>
    </div>
    </ProtectedComponent>
  );
};

export default VideoSync;
